/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as ActionTypes from "constants/ActionTypes";
import { ParticipantAction } from "types/Actions";
import {
  Participant,
  ParticipantSet,
  WebRTCConnectionState,
  WebsocketConnectionState,
} from "types/Participant";
import { updateIfExists } from "reducers/utilities/UpdateIfExists";
import { AdobeId } from "reducers/CurrentUserReducer";

// TODO - this number imposes the size of the `micSamples` array, which then implicitly determines the UI width of the Meter component
//  `micSamples` should default to empty and Meter should own its width at the UI level
const PARTICIPANT_WAVEFORM_NUM_BARS = 83;

function buildNewMicSamples(): number[] {
  return new Array(PARTICIPANT_WAVEFORM_NUM_BARS).fill(0);
}

const INITIAL_CONNECTION_STATE = {
  websocketConnectionState: WebsocketConnectionState.DISCONNECTED,
  webRtcConnectionState: WebRTCConnectionState.NEW,
  streamActive: false,
};

export default function ParticipantsReducer(
  participants: ParticipantSet,
  action: ParticipantAction
): ParticipantSet {
  switch (action.type) {
    case ActionTypes.PARTICIPANT_MUTED:
      return {
        ...participants,
        [action.from]: {
          ...participants[action.from],
          muted: true,
        },
      };
    case ActionTypes.PARTICIPANT_UNMUTED:
      return {
        ...participants,
        [action.from]: {
          ...participants[action.from],
          muted: false,
        },
      };
    case ActionTypes.PARTICIPANT_LEFT_PROJECT:
      const { [action.from]: _, ...remaining } = participants;
      return remaining;
    case ActionTypes.PARTICIPANT_WEBSOCKET_UNSUBSCRIBED:
      return updateIfExists(participants, action.from, {
        websocketConnectionState: WebsocketConnectionState.DISCONNECTED,
      });
    case ActionTypes.PARTICIPANT_WEBSOCKET_SUBSCRIBED: {
      const participant = participants[action.from];
      return {
        ...participants,
        [action.from]: {
          ...INITIAL_CONNECTION_STATE,
          ...participant,
          ...action.profile,
          websocketConnectionState: WebsocketConnectionState.CONNECTED,
          micSamples: participant?.micSamples ?? buildNewMicSamples(),
        },
      };
    }
    case ActionTypes.PARTICIPANT_WEBSOCKET_CONNECTING:
      return updateIfExists(participants, action.from, {
        websocketConnectionState: WebsocketConnectionState.CONNECTING,
      });
    case ActionTypes.PARTICIPANT_WEBRTC_CONNECTION_CHANGED: {
      return updateIfExists<Participant>(participants, action.from, {
        webRtcConnectionState: action.webRtcConnectionState,
      });
    }
    case ActionTypes.PARTICIPANT_RECEIVED_TRACK_UPDATE:
      return updateIfExists<Participant>(participants, action.from, {
        streamActive: action.streamActive,
      });
    case ActionTypes.PARTICIPANT_MIC_SAMPLE:
      const micSamples =
        participants[action.from]?.micSamples ?? buildNewMicSamples();
      return {
        ...participants,
        [action.from]: {
          ...participants[action.from],
          micSamples: [...micSamples, action.sample].slice(
            -PARTICIPANT_WAVEFORM_NUM_BARS
          ),
        },
      };
    case ActionTypes.RECORDING_STARTING:
      return Object.keys(participants).reduce(
        (result: ParticipantSet, key: AdobeId) => {
          const participant = participants[key];
          result[key] = {
            ...participant,
            micSamples: new Array(PARTICIPANT_WAVEFORM_NUM_BARS).fill(0),
          };
          return result;
        },
        {}
      );
    default:
      return participants;
  }
}
